import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { usersApi, rolesApi } from './api'
import { handleApiError } from '@/lib/api/axios'
import api from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { User, Role } from '@/types/entities'
import type { ListParams } from '@/lib/api/types'

// Query keys
export const userKeys = {
    all: ['users'] as const,
    lists: () => [...userKeys.all, 'list'] as const,
    list: (params: ListParams) => [...userKeys.lists(), params] as const,
    details: () => [...userKeys.all, 'detail'] as const,
    detail: (id: number) => [...userKeys.details(), id] as const,
}

export const roleKeys = {
    all: ['roles'] as const,
    lists: () => [...roleKeys.all, 'list'] as const,
    list: (params: ListParams) => [...roleKeys.lists(), params] as const,
    details: () => [...roleKeys.all, 'detail'] as const,
    detail: (id: number) => [...roleKeys.details(), id] as const,
}

// Users hooks
export function useUsers(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: userKeys.list(params || {}),
        queryFn: () => usersApi.list(params),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useUser(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: userKeys.detail(id),
        queryFn: () => usersApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateUser() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'mfa'> & { password: string; mfaRequired?: boolean; mfaMethods?: string[] }) =>
            usersApi.create(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: userKeys.lists() })
            toast({
                title: 'User Created',
                description: `${response.data.name} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateUser() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<User, 'id' | 'createdAt' | 'updatedAt' | 'mfa'> & { mfaRequired?: boolean; mfaMethods?: string[] }> }) =>
            usersApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: userKeys.lists() })
            queryClient.invalidateQueries({ queryKey: userKeys.detail(variables.id) })
            toast({
                title: 'User Updated',
                description: `${response.data.name} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useChangeUserPassword() {
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, newPassword, currentPassword }: { id: number; newPassword: string; currentPassword?: string }) =>
            usersApi.changePassword(id, { newPassword, currentPassword }),
        onSuccess: () => {
            toast({
                title: 'Password changed',
                description: 'The user password has been updated successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteUser() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => usersApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: userKeys.lists() })
            queryClient.removeQueries({ queryKey: userKeys.detail(id) })
            toast({
                title: 'User Deleted',
                description: 'User has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Roles hooks
export function useRoles(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: roleKeys.list(params || {}),
        queryFn: () => rolesApi.list(params),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired,
    })
}

export function useRole(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: roleKeys.detail(id),
        queryFn: () => rolesApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateRole() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Role, 'id'>) =>
            rolesApi.create(data),
        onSuccess: (response) => {
            queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
            toast({
                title: 'Role Created',
                description: `${response.data.name} has been created successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useUpdateRole() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Role, 'id'>> }) =>
            rolesApi.update(id, data),
        onSuccess: (response, variables) => {
            queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
            queryClient.invalidateQueries({ queryKey: roleKeys.detail(variables.id) })
            toast({
                title: 'Role Updated',
                description: `${response.data.name} has been updated successfully.`,
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

export function useDeleteRole() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => rolesApi.delete(id),
        onSuccess: (_, id) => {
            queryClient.invalidateQueries({ queryKey: roleKeys.lists() })
            queryClient.removeQueries({ queryKey: roleKeys.detail(id) })
            toast({
                title: 'Role Deleted',
                description: 'Role has been deleted successfully.',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: handleApiError(error),
                variant: 'destructive',
            })
        },
    })
}

// Get available permissions
export function usePermissions() {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery({
        queryKey: [...roleKeys.all, 'permissions'],
        queryFn: async () => {
            // Call the actual permissions API endpoint
            const response = await api.get('/api/roles/permissions/all')
            return response.data
        },
        enabled: isAuthenticated && !mfaRequired,
        staleTime: 5 * 60 * 1000, // 5 minutes (permissions can change when populated)
    })
}



